import { useState, useCallback } from 'react';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import { docxExport } from '../../DisasterGPT/DisasterGPTDataHelpers/DGPTExport';
import { isAnalyticsSection } from 'components/DisasterGPT/constants/opordSectionDefaults';
import { isVirtualAnalyticsLayerSection } from 'components/Reports/document/expandAnalyticsLayerSections';
import { nestSitrepSections } from 'components/Reports/document/nestSitrepSections';
import { readableSectionHtml } from 'components/Reports/utils/readableReportSection';
import type { SitrepSectionForExport } from '../../DisasterGPT/hooks/useSitrepPdfExport';
import { useSitrepPdfExport } from '../../DisasterGPT/hooks/useSitrepPdfExport';
import { GetSitrepsResponse } from 'components/Reports/reports.types';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import {
  PollSitrepResponse,
  SitrepImage,
  AttachedFile,
  useRetrieveSitrepSectionImages,
  fetchAttachedFilesForSitrep,
} from '../../DisasterGPT/hooks/useSitrepsV2';
import { observability } from 'utils/observability';

// Helper function to escape HTML special characters in plain text
function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type AttachmentForExport = {
  file_name: string;
  url: string | null;
};

/**
 * Builds an HTML bullet list linking to attached files. Used to surface SITREP
 * file attachments in exported PDF/DOCX documents.
 */
export function buildAttachmentsListHtml(
  attachments: AttachmentForExport[] | null | undefined
): string {
  if (!attachments || attachments.length === 0) return '';

  const items = attachments
    .map(({ file_name, url }) => {
      const label = escapeHtml(file_name || 'Untitled file');
      return url
        ? `<li><a href="${url}">${label}</a></li>`
        : `<li>${label}</li>`;
    })
    .join('');

  return `<ul>${items}</ul>`;
}

/**
 * Resolves signed download URLs for a SITREP's attached files and returns a
 * ready-to-use "Attachments" content string for export (PDF/DOCX). Returns null
 * when there are no attachments to include.
 */
export async function buildSitrepAttachmentsContent(
  attachedFiles: AttachedFile[] | null | undefined,
  getSignedUrl: (fileUrl: string) => Promise<string>
): Promise<string | null> {
  if (!attachedFiles || attachedFiles.length === 0) return null;

  const withUrls = await Promise.all(
    attachedFiles.map(async (file) => {
      try {
        const url = await getSignedUrl(file.fileUrl);
        return { file_name: file.file_name, url };
      } catch (err) {
        observability.captureException(err, {
          context: 'buildSitrepAttachmentsContent',
          fileName: file.file_name,
        });
        return { file_name: file.file_name, url: null };
      }
    })
  );

  return buildAttachmentsListHtml(withUrls);
}

/** Hook for fetching full sitrep data with sections from the API */
function useFetchFullSitrepData() {
  const fetchFullSitrepData = useCallback(
    async (sitrep: GetSitrepsResponse): Promise<PollSitrepResponse | null> => {
      try {
        const response = await axios.get<PollSitrepResponse>(
          `${config.apiGateway.sitrepsV2}/pollSitrep`,
          { params: { sitrep_id: sitrep.id } }
        );
        return response.data;
      } catch (err) {
        console.warn(`Failed to fetch sitrep ${sitrep.id}:`, err);
        return null;
      }
    },
    []
  );

  return { fetchFullSitrepData };
}

interface UseExportAllSitrepsReturn {
  exportAllSitreps: (
    sitreps: GetSitrepsResponse[],
    filterName: string | null,
    format: 'pdf' | 'docx'
  ) => Promise<void>;
  isExporting: boolean;
}

/** Hook for exporting multiple sitreps as a single PDF or DOCX document */
export function useExportAllSitreps(): UseExportAllSitrepsReturn {
  const [isExporting, setIsExporting] = useState(false);
  const { exportPdf } = useSitrepPdfExport();
  const { fetchFullSitrepData } = useFetchFullSitrepData();
  const { mutateAsync: getSignedUrl } = useSignedUrl();

  // TODO (aeortic): I need an enpoint that can get the images for a section.
  // We now have a folder id column for the section table, so this should be possible.
  const { mutateAsync: fetchImages } = useRetrieveSitrepSectionImages();

  const exportAllSitreps = useCallback(
    async (
      sitreps: GetSitrepsResponse[],
      filterName: string | null,
      format: 'pdf' | 'docx'
    ): Promise<void> => {
      if (sitreps.length === 0) {
        toast.warning('No reports to export');
        return;
      }

      setIsExporting(true);
      try {
        const timestamp = dayjs().format('YYYY-MM-DD hh-mm A');
        const baseName = `Reports_${filterName || 'All'}_${timestamp}`;

        // Combine all sitreps into one document
        // Each sitrep becomes a header followed by its sections
        const allSections: SitrepSectionForExport[] = [];

        // Fetch all sitreps' full data and attached files in parallel for better performance
        const [fullSitreps, sitrepsAttachedFiles] = await Promise.all([
          Promise.all(sitreps.map((sitrep) => fetchFullSitrepData(sitrep))),
          Promise.all(
            sitreps.map((sitrep) =>
              fetchAttachedFilesForSitrep(sitrep.id, sitrep.group_guid)
            )
          ),
        ]);

        // Process each sitrep and its fetched data
        for (let i = 0; i < sitreps.length; i++) {
          const sitrep = sitreps[i];
          const fullSitrep = fullSitreps[i];

          // Add sitrep name as a header (mark as report boundary for horizontal line)
          allSections.push({
            name: sitrep.name || 'Untitled Report',
            content: sitrep.description || '',
            timestamp: sitrep.timestamp
              ? new Date(sitrep.timestamp).toISOString()
              : '',
            isReportBoundary: true,
          });

          // Category, then each nested section, so Analytics layers read in order.
          if (fullSitrep?.sections?.length) {
            for (const root of nestSitrepSections(fullSitrep.sections)) {
              const version = root.section_versions?.[0];
              const skipBody =
                isAnalyticsSection(root) && root.children.length > 0;
              allSections.push({
                sectionId: isVirtualAnalyticsLayerSection(root)
                  ? undefined
                  : root.id,
                name: root.name,
                content: skipBody
                  ? '<p></p>'
                  : readableSectionHtml({
                      sitrep_section_text: version?.sitrep_section_text,
                      data_string: version?.data_string,
                    }),
                depth: 0,
              });
              for (const child of root.children) {
                const childVersion = child.section_versions?.[0];
                allSections.push({
                  sectionId: isVirtualAnalyticsLayerSection(child)
                    ? undefined
                    : child.id,
                  name: child.name,
                  content: readableSectionHtml({
                    sitrep_section_text: childVersion?.sitrep_section_text,
                    data_string: childVersion?.data_string,
                  }),
                  depth: 1,
                });
              }
            }
          }

          // Add an "Attachments" section listing this sitrep's files, if any
          const attachmentsContent = await buildSitrepAttachmentsContent(
            sitrepsAttachedFiles[i],
            getSignedUrl
          );
          if (attachmentsContent) {
            allSections.push({
              name: 'Attachments',
              content: attachmentsContent,
            });
          }
        }

        if (format === 'pdf') {
          const blob = await exportPdf(
            allSections,
            `All Reports - ${filterName || 'All'}`
          );
          saveAs(blob, `${baseName}.pdf`);
          toast.success('PDF downloaded');
        } else {
          const data = await Promise.all(
            allSections.map(async (section) => {
              let images: SitrepImage[] = [];

              if (section.sectionId) {
                images = await fetchImages(section.sectionId || '');
              }

              return {
                sectionId: section.sectionId,
                role: section.name,
                content: section.content,
                timestamp: section.timestamp,
                isReportBoundary: section.isReportBoundary,
                depth: section.depth,
                images: images || [],
              };
            })
          );

          await docxExport(data, 'Download', baseName);
          toast.success('Word document downloaded');
        }
      } catch (error) {
        console.error('Failed to export all:', error);
        toast.error(`Failed to export as ${format.toUpperCase()}`);
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    [exportPdf, fetchFullSitrepData, fetchImages, getSignedUrl]
  );

  return { exportAllSitreps, isExporting };
}

/** Export one current, historical, or draft report. */
export function useExportSitrep() {
  const { exportAllSitreps, isExporting } = useExportAllSitreps();
  const exportSitrep = useCallback(
    async (sitrep: GetSitrepsResponse, format: 'pdf' | 'docx') => {
      await exportAllSitreps([sitrep], sitrep.name || 'Report', format);
    },
    [exportAllSitreps]
  );
  return { exportSitrep, isExporting };
}
